/* eslint-disable formatjs/no-literal-string-in-jsx */
import React, { useCallback } from 'react'
import { intl } from 'lang/intl'
import PropTypes from 'prop-types'
import { get, isFunction, partial } from 'lodash-es'
import { connect } from 'react-redux'
import { getFormValues, Field } from 'redux-form/dist/redux-form'
import { useCopyToClipboard } from 'react-use'

import { TtIconButton } from '@23/react'

import { reduxFormValidators } from '../../../../utils'
import withFocus from '../../../withFocus'
import {
  Row,
  Col,
  UploadAvatarComponent,
  ReduxFormSimpleFieldComponent,
  ToggleFormAdapterComponent,
} from '../../../../components'
import { showNotice } from '../../../../../Layout/Notices/Notices'

import './SpeakerFormContainerComponent.scss'

const { required, validateEmail } = reduxFormValidators
// add focus after 200 so the modal can be opened or the inline editing window is slighted in already
const fieldWithFocus = withFocus(ReduxFormSimpleFieldComponent, { delay: 200 })
const linkRef = React.createRef()

const renderNewModeForm = (modalVisible) => {
  return (
    <Row type="flex" gutter={16}>
      <Col xs={12} sm={24}>
        <Field
          name="name"
          label="Name*"
          component={fieldWithFocus}
          type="text"
          validate={required}
          addFocus={modalVisible}
        />
      </Col>
      <Col xs={12} sm={24}>
        <Field
          name="email"
          component={ReduxFormSimpleFieldComponent}
          type="email"
          label="Email*"
          validate={[required, validateEmail]}
        />
      </Col>
    </Row>
  )
}

const getInvitationLinkProps = () => ({
  readOnly: true,
  onFocus: () => isFunction(linkRef.current.select) && linkRef.current.select(),
})

const handleAvatarChange = (onChange, file, imageSrc) => {
  onChange('avatar_url', imageSrc)
  onChange('avatarImageFile', file)
}

const renderEditModeForm = (
  modalVisible,
  change,
  formValues,
  handleCopyRoomLink
) => {
  return (
    <Row type="flex" gutter={16}>
      <Col xs={24}>
        <div className="edit-mode-base-fields-wrapper">
          <UploadAvatarComponent
            size={[48, 48]}
            name={get(formValues, 'name')}
            imageSrc={get(formValues, 'avatar_url')}
            onChange={partial(handleAvatarChange, change)}
          />
          <div className="base-fields">
            <Field
              name="name"
              label="Name*"
              component={fieldWithFocus}
              type="text"
              validate={required}
              addFocus={modalVisible}
            />
            <Field
              name="email"
              component={ReduxFormSimpleFieldComponent}
              type="email"
              label="Email*"
              validate={[required, validateEmail]}
            />
          </div>
        </div>
      </Col>
      <Col xs={24}>
        <Field
          className="bio-field"
          name="bio"
          component={ReduxFormSimpleFieldComponent}
          type="textarea"
          label="Bio"
          placeholder="Tell your audience why this speaker is a part of this webinar? What can they learn from this speaker? What has this speaker previously accomplished?"
        />
      </Col>
      <Col xs={12}>
        <Field
          name="title"
          component={ReduxFormSimpleFieldComponent}
          type="text"
          label="Title"
        />
      </Col>
      <Col xs={12}>
        <Field
          name="link"
          component={ReduxFormSimpleFieldComponent}
          type="text"
          label="Website"
        />
      </Col>
      <Col xs={12}>
        <Field
          name="company"
          component={ReduxFormSimpleFieldComponent}
          type="text"
          label="Company"
        />
      </Col>
      <Col xs={12}>
        <Field
          name="facebook"
          component={ReduxFormSimpleFieldComponent}
          type="text"
          label="Facebook"
        />
      </Col>
      <Col xs={12}>
        <Field
          name="twitter"
          component={ReduxFormSimpleFieldComponent}
          type="text"
          label="Twitter"
        />
      </Col>
      <Col xs={12}>
        <Field
          name="linkedin"
          component={ReduxFormSimpleFieldComponent}
          type="text"
          label="LinkedIn"
        />
      </Col>
      <Col xs={24}>
        <div className="invitation-link">
          <div className="invitation-link-field">
            <Field
              name="room_link"
              component={ReduxFormSimpleFieldComponent}
              type="text"
              label="Invitation Link"
              elementRef={linkRef}
              customInputProps={getInvitationLinkProps()}
            />
          </div>
          <div className="copy-link">
            <TtIconButton
              className="room-link-copy-button"
              label={intl.formatMessage({
                defaultMessage: 'Copy link',
              })}
              name="duplicate"
              library="one"
              onClick={handleCopyRoomLink}
              disabled={!get(formValues, 'room_link')}
            />
          </div>
        </div>
      </Col>
      <Col xs={24}>
        <div className="toggle-row top-border-row">
          <h3>Show on registration page</h3>
          <Field
            name="hidden_p"
            component={ToggleFormAdapterComponent}
            format={(value) => {
              // Format the number coming from the API into the expected type by
              //  the form.
              return !value
            }}
            parse={(value) => {
              // Parse the boolean coming from the form into the expected type
              //  by the API.
              return value ? 0 : 1
            }}
          />
        </div>
      </Col>
      <Col xs={24}>
        <div className="toggle-row top-border-row">
          <h3>Show TvCards</h3>
          <Field
            name="show_title_card_p"
            component={ToggleFormAdapterComponent}
            format={(value) => {
              return Boolean(value)
            }}
            parse={(value) => {
              return value ? 1 : 0
            }}
          />
        </div>
      </Col>
    </Row>
  )
}

const SpeakerFormContainerComponent = ({
  modalVisible,
  change,
  mode,
  formValues,
}) => {
  const roomLink = get(formValues, 'room_link', '')
  const [, copyToClipboard] = useCopyToClipboard()

  const handleCopyRoomLink = useCallback(() => {
    if (!roomLink) {
      return
    }

    copyToClipboard(roomLink)
    showNotice({
      expirationMillis: 2000,
      isClosable: true,
      message: intl.formatMessage({
        defaultMessage: 'Link copied!',
      }),
    })
  }, [copyToClipboard, roomLink])

  return (
    <div className="speaker-form-component">
      {mode === 'new' && renderNewModeForm(modalVisible)}
      {mode === 'edit' &&
        renderEditModeForm(
          modalVisible,
          change,
          formValues,
          handleCopyRoomLink
        )}
    </div>
  )
}

SpeakerFormContainerComponent.propTypes = {
  change: PropTypes.func,
  modalVisible: PropTypes.bool,
  // TODO (noahlaux): Refactor with TS
  // eslint-disable-next-line react/no-unused-prop-types
  formName: PropTypes.string,
  // TODO (noahlaux): Refactor with TS
  // eslint-disable-next-line react/no-unused-prop-types
  name: PropTypes.string,
  // TODO (noahlaux): Refactor with TS
  // eslint-disable-next-line react/no-unused-prop-types
  avatarImageSrc: PropTypes.string,
  // eslint-disable-next-line react/forbid-prop-types
  formValues: PropTypes.object,
  mode: PropTypes.oneOf(['new', 'edit']),
}

const mapStateProps = (state, props) => ({
  formValues: getFormValues(props.formName)(state),
})

// eslint-disable-next-line import-x/no-default-export
export default connect(mapStateProps)(SpeakerFormContainerComponent)
export { SpeakerFormContainerComponent }
